import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import EntityLogo from "@/components/EntityLogo";
import { Search, Plus, X, Check } from "lucide-react";

const COUNTRIES = ["Canada", "United States", "Other"];
const DIVISIONS = ["8U", "9U", "10U", "11U", "12U", "13U", "14U", "15U", "16U", "17U", "18U", "College", "Senior"];

// Reusable searchable Current-Team selector with inline create.
// value = current_team_id; initialTeam = {id,name,...} for label rehydration.
// onChange(team|null) — team includes {id, name, ...}.
export default function TeamSelector({ value, initialTeam, onChange, testidPrefix = "team" }) {
  const [selected, setSelected] = useState(initialTeam || null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ name: "", age_division: "", country: "Canada", province: "", state: "", city: "" });

  useEffect(() => { if (initialTeam) setSelected(initialTeam); }, [initialTeam?.id]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/live/teams/search", { params: { q: term } })
        .then(({ data }) => setResults(data.results || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const pick = (team) => {
    setSelected(team);
    onChange && onChange(team);
    setQ(""); setResults(null); setShowCreate(false); setDuplicates(null);
  };

  const clear = () => { setSelected(null); onChange && onChange(null); };

  const submitCreate = async (force = false) => {
    setErr("");
    if (!form.name.trim()) { setErr("Team name is required"); return; }
    setCreating(true);
    try {
      const payload = { ...form, force };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      payload.name = form.name.trim();
      const { data } = await api.post("/live/teams/quick-create", payload);
      if (data.created) { pick(data.team); }
      else { setDuplicates(data.duplicates || []); }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setCreating(false); }
  };

  if (selected) {
    return (
      <div data-testid={`${testidPrefix}-selected`} className="flex items-center gap-3 p-3 border border-[#0A192F] bg-slate-50 rounded-sm">
        <EntityLogo src={selected.effective_logo_url} alt={selected.name} size="w-10 h-10" textSize="text-[9px]" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">{selected.name}</div>
          <div className="text-xs text-slate-500 truncate">{[selected.organization_name, selected.age_division, selected.province || selected.state, selected.country].filter(Boolean).join(" · ") || "—"}</div>
        </div>
        <button type="button" data-testid={`${testidPrefix}-change`} onClick={clear} className="shrink-0 text-slate-400 hover:text-[#E63946]" aria-label="Change team"><X className="w-4 h-4" /></button>
      </div>
    );
  }

  return (
    <div data-testid={`${testidPrefix}-selector`}>
      {!showCreate && (
        <>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid={`${testidPrefix}-search-input`} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search your current team…" className="w-full pl-9 pr-3 py-3 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#0A192F]" />
          </div>
          {q.trim().length >= 2 && (
            <div className="mt-2 space-y-2" data-testid={`${testidPrefix}-search-results`}>
              {searching && <div className="text-sm text-slate-400 py-1">Searching…</div>}
              {!searching && (results || []).map((r) => (
                <button type="button" key={r.id} data-testid={`${testidPrefix}-result-${r.id}`} onClick={() => pick(r)}
                  className="w-full flex items-center gap-3 p-2.5 border border-slate-200 rounded-sm text-left hover:border-[#0A192F]">
                  <EntityLogo src={r.effective_logo_url} alt={r.name} size="w-8 h-8" textSize="text-[8px]" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{r.name}</div>
                    <div className="text-xs text-slate-500 truncate">{[r.organization_name, r.age_division, r.province || r.state, r.country].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                </button>
              ))}
              {!searching && results && results.length === 0 && (
                <div data-testid={`${testidPrefix}-no-results`} className="text-sm text-slate-500 py-1">No matching team found.</div>
              )}
            </div>
          )}
          <button type="button" data-testid={`${testidPrefix}-create-toggle`} onClick={() => { setShowCreate(true); setForm((f) => ({ ...f, name: q.trim() || f.name })); }}
            className="mt-2 text-sm font-semibold text-[#E63946] uppercase tracking-wider inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Create New Team</button>
        </>
      )}

      {showCreate && (
        <div data-testid={`${testidPrefix}-create-form`} className="space-y-3 border border-slate-200 rounded-sm p-3">
          {err && <div className="text-xs text-[#E63946]">{err}</div>}
          <input data-testid={`${testidPrefix}-create-name`} placeholder="Team name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid={`${testidPrefix}-create-division`} value={form.age_division} onChange={(e) => setForm({ ...form, age_division: e.target.value })} className="px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="">Age division</option>
              {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select data-testid={`${testidPrefix}-create-country`} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white">
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input data-testid={`${testidPrefix}-create-region`} placeholder={form.country === "United States" ? "State" : "Province"} value={form.country === "United States" ? form.state : form.province} onChange={(e) => setForm(form.country === "United States" ? { ...form, state: e.target.value } : { ...form, province: e.target.value })} className="px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
            <input data-testid={`${testidPrefix}-create-city`} placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
          </div>

          {duplicates && duplicates.length > 0 && (
            <div data-testid={`${testidPrefix}-duplicates`} className="border-l-4 border-amber-400 bg-amber-50 p-3 space-y-2">
              <div className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Possible existing teams — use one instead?</div>
              {duplicates.map((d) => (
                <button type="button" key={d.id} data-testid={`${testidPrefix}-dup-${d.id}`} onClick={() => pick(d)} className="w-full flex items-center justify-between gap-2 p-2 bg-white border border-slate-200 rounded-sm text-left hover:border-[#0A192F]">
                  <span className="text-sm truncate">{d.name} <span className="text-xs text-slate-400">{[d.age_division, d.province || d.state, d.country].filter(Boolean).join(" · ")}</span></span>
                  <Check className="w-4 h-4 text-[#0A192F] shrink-0" />
                </button>
              ))}
              <button type="button" data-testid={`${testidPrefix}-create-anyway`} onClick={() => submitCreate(true)} disabled={creating} className="text-xs font-semibold text-[#E63946] uppercase tracking-wider">Create as new team anyway</button>
            </div>
          )}

          <div className="flex gap-2">
            <button type="button" onClick={() => { setShowCreate(false); setDuplicates(null); setErr(""); }} className="px-4 py-2 border border-slate-300 text-xs font-semibold uppercase tracking-wider rounded-sm">Cancel</button>
            {!(duplicates && duplicates.length > 0) && (
              <button type="button" data-testid={`${testidPrefix}-create-submit`} onClick={() => submitCreate(false)} disabled={creating} className="px-4 py-2 bg-[#0A192F] text-white text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">{creating ? "Saving…" : "Create & Select"}</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
